import { useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { isEmpty } from 'ramda'
import { gql, useQuery } from '@apollo/client'
import { useDialogController } from '@machobear/design-system'
import { useFeatureFlagVariantKey } from 'posthog-js/react'

import { useCityData, useNeighbourhoodData } from '@/lib/locations'
import { getFilterSlugData, getUrlSlugs } from '@/utils/location'
import { useAlertModalContext } from '@/hooks/use-alert-modal-context'
import { useFilters } from '@/hooks/use-filters'
import useSessionComplete from '@/hooks/use-session-complete'
import useCreateListingAlert from '@/features/Listings/hooks/use-create-listing-alert'
import useListingsFavourites from '@/features/Listings/hooks/use-listings-favourites'
import { injectAds, useResponsiveAdValues } from '@/components/adsense'
import { AlertConfirmationModal } from '@/components/alert'

import { useMapList } from '../maplist-context'
import { List as ListingsList } from './list'

import 'react-indiana-drag-scroll/dist/style.css'

const BUILDING_DATA = gql`
  fragment mapBuildingListFields on Listing {
    bathrooms
    bedrooms
    gr_min_price
    gr_max_price
    gr_unit
    gr_count
    allow_applications
    public_viewing
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    unit_subtype_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    size
    unit_files {
      aws_s3_key
      position
      tag
      description
    }
    landlords {
      identity_verified_state
    }
    search_deals
    previous_price
    price_change_status
    sponsored
  }
  fragment mapListViewFields on FullListing {
    street_name
    building_type
    building_name
    building_id
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_size
    gr_min_price
    gr_max_price
    allow_applications
    public_viewing
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    unit_subtype_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    unit_files {
      aws_s3_key
      position
      tag
      description
    }
    landlords {
      identity_verified_state
    }
    search_deals
    previous_price
    price_change_status
    sponsored
    super_boost
  }
  query ($input: ListSearchInput!) {
    listSearch {
      buildings(input: $input) {
        metadata {
          page
          page_size
          total_count
          listings_count
        }
        feed {
          building_id
          street_name
          building_type
          full_street_name
          city
          state
          zip
          building_name
          location {
            lat
            lon
          }
          building_files {
            aws_s3_key
            tag
            description
          }
          listings {
            ...mapBuildingListFields
          }
          listing_count
          # score
        }
      }
      featured(input: $input) {
        ...mapListViewFields
      }
    }
  }
`

const BUILDING_DATA_V2 = gql`
  fragment featuredListingFields on FullListing {
    street_name
    building_type
    building_name
    building_id
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_size
    gr_min_price
    gr_max_price
    allow_applications
    public_viewing
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    unit_subtype_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    unit_files {
      aws_s3_key
      position
      tag
      description
    }
    landlords {
      identity_verified_state
    }
    search_deals
    previous_price
    price_change_status
    sponsored
    score
    super_boost
  }
  fragment buildingListingFields on Listing {
    bathrooms
    bedrooms
    gr_min_price
    gr_max_price
    gr_unit
    gr_count
    allow_applications
    public_viewing
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    unit_subtype_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    size
    unit_files {
      aws_s3_key
      position
      tag
      description
    }
    landlords {
      identity_verified_state
    }
    search_deals
    previous_price
    price_change_status
    sponsored
  }
  query ($input: ListSearchInput!) {
    newListSearch {
      feed(input: $input) {
        featured {
          ...featuredListingFields
        }
        buildings {
          building_id
          street_name
          building_type
          full_street_name
          city
          state
          zip
          building_name
          location {
            lat
            lon
          }
          building_files {
            aws_s3_key
            tag
            description
          }
          listing_count
          score
          listings {
            ...buildingListingFields
          }
        }
        metadata {
          page
          page_size
          total_count
          listings_count
          distance_expanded
        }
      }
    }
  }
`

const randomizeListingsByScore = listings => {
  if (!listings || listings.length === 0) return listings

  const groupedByScore = {}
  listings.forEach(listing => {
    const score = listing.score || 0
    if (!groupedByScore[score]) {
      groupedByScore[score] = []
    }
    groupedByScore[score].push(listing)
  })
  const sortedScores = Object.keys(groupedByScore).sort((a, b) => b - a)

  const randomizedListings = []
  sortedScores.forEach(score => {
    const group = [...groupedByScore[score]]
    for (let i = group.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[group[i], group[j]] = [group[j], group[i]]
    }
    randomizedListings.push(...group)
  })

  return randomizedListings
}

const MapList = ({
  boundaries,
  onlyVerifiedListings,
  markersLoaded,
  customSearchCityName,
  isCampus,
  ...rest
}) => {
  const searchParams = useSearchParams()
  const { showModal, closeModal } = useDialogController()
  const { isListVisible } = useMapList()
  const { modalVisible, setModalVisible } = useAlertModalContext()
  useListingsFavourites()
  useCreateListingAlert(showModal)
  const isLoggedIn = useSessionComplete()
  const [filters, setFilter, , isFilterSet] = useFilters()
  const router = useRouter()
  const { citySlug, neighbourhoodSlug } = getUrlSlugs(router)
  const neighborhood = useNeighbourhoodData(neighbourhoodSlug, citySlug)
  const { data: cityData, loading: cityLoading } = useCityData(citySlug)
  const filterSlugData = getFilterSlugData({ router })
  const filterSlug = filterSlugData?.slug
  const { adInterval } = useResponsiveAdValues()

  const variant = useFeatureFlagVariantKey('search_algorithm_v2')
  const variantLoading = !isCampus && variant === undefined
  const isV2 = isCampus ? false : variant === 'v2'
  const BUILDING_DATA_QUERY = isV2 ? BUILDING_DATA_V2 : BUILDING_DATA

  const buildingDataInput = {
    ...filters,
    ...(onlyVerifiedListings ? { search_only_verified: true } : {}),
    ...(customSearchCityName ? { cities: [customSearchCityName] } : {}),
    ...boundaries,
    page_size: 48,
    useCollapse: searchParams.get('useCollapse') !== 'false'
  }

  const {
    previousData = {},
    data = previousData,
    loading: loadingBuildings
  } = useQuery(BUILDING_DATA_QUERY, {
    skip: variantLoading || (!isListVisible && isEmpty(boundaries)),
    variables: {
      input: buildingDataInput
    },
    pollInterval: 0,
    fetchPolicy: 'cache-first'
  })

  // Process data based on variant
  let featured, buildings, metadata, buildingsWithAds

  if (isV2) {
    const {
      newListSearch: {
        feed: {
          featured: v2Featured = [],
          buildings: v2Buildings = [],
          metadata: v2Metadata = {}
        } = {}
      } = {}
    } = data

    featured = v2Featured
    buildings = v2Buildings
    metadata = v2Metadata
  } else {
    const {
      listSearch: {
        buildings: { feed: v1Buildings = [], metadata: v1Metadata = {} } = {},
        featured: v1Featured
      } = {}
    } = data

    featured = v1Featured
    buildings = v1Buildings
    metadata = v1Metadata
  }

  // When using the Suggested sort, we randomize the order of featured listings
  // with the same score to help distribute impressions more evenly
  const randomizedFeatured = useMemo(() => {
    if (
      isV2 &&
      (!filters.sort?.sort_by || filters.sort?.sort_by === 'SUGGESTED')
    ) {
      return randomizeListingsByScore(featured)
    }
    return featured
  }, [featured, filters, isV2])

  // Process buildings with ads based on variant
  if (isV2) {
    const allListings = [...randomizedFeatured, ...buildings]
    buildingsWithAds = injectAds(allListings, adInterval)
  } else {
    buildingsWithAds = injectAds(buildings, adInterval)
  }

  if (variantLoading) return null

  return (
    <>
      <ListingsList
        onlyVerifiedListings={onlyVerifiedListings}
        featured={randomizedFeatured}
        buildings={buildings}
        buildingsWithAds={buildingsWithAds}
        loadingBuildings={loadingBuildings}
        metadata={metadata}
        markersLoaded={markersLoaded}
        data={data}
        neighborhood={neighborhood}
        city={cityData}
        cityLoading={cityLoading}
        citySlug={citySlug}
        filterSlug={filterSlug}
        filter={{ filters, setFilter, isFilterSet }}
        isCampus={isCampus}
        {...rest}
      />
      <AlertConfirmationModal
        isLoggedIn={isLoggedIn}
        open={modalVisible}
        onClose={() => {
          setModalVisible(false)
          closeModal()
        }}
      />
    </>
  )
}

MapList.displayName = 'MapList'

export default MapList
